import { useTranslation } from 'react-i18next';
import { useEmployerForm } from '../hooks/useEmployerForm';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import CustomDatePicker from '@shared/components/CustomDatePicker';

export default function EmployerForm({ person }) {
    const { t } = useTranslation();
    const {
        register,
        errors,
        handleSubmit,
        onSubmit,
        isEditing,
        employerData,
        handleEdit,
        handleDelete,
        handleCancel,
        countries,
        handleCountryChange,
        selectedCountry,
        watch,
        setValue
    } = useEmployerForm(person);

    // Helper function to format dates
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        }
        return dateString;
    };

    return (
        <div className="employer-form-container">
            <h3 className="form-subtitle">
                {t('persons.employer.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' ')
                })}
            </h3>

            <div className="form-content">
                <div className="form-section">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-row">
                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.employer.startDate')}<span className="required">*</span></span>
                                <div className="form-input-fields">
                                    <CustomDatePicker
                                        name="startDate"
                                        value={watch('startDate')}
                                        onChange={(date) => {
                                            setValue('startDate', date, { 
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            });
                                        }}
                                        placeholderText={t('common.dateFormat')}
                                        errors={errors}
                                        maxDate={new Date()}
                                    />
                                </div>
                            </div>

                            <div className="form-input-wrapper">
                                <span className="span-bold">{t('persons.employer.endDate')}<span className="required">*</span></span>
                                <div className="form-input-fields">
                                    <CustomDatePicker
                                        name="endDate"
                                        value={watch('endDate')}
                                        onChange={(date) => {
                                            setValue('endDate', date, { 
                                                shouldValidate: true,
                                                shouldDirty: true,
                                                shouldTouch: true
                                            });
                                        }}
                                        placeholderText={t('common.dateFormat')}
                                        errors={errors}
                                        maxDate={new Date()}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.employer.position')}<span className="required">*</span></span>
                            <div className="form-input-fields">
                                <input
                                    type="text"
                                    className={`form-control ${errors.position ? 'is-invalid' : ''}`}
                                    {...register('position')}
                                />
                                {errors.position && (
                                    <div className="invalid-feedback">{errors.position.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.employer.employer')}<span className="required">*</span></span>
                            <div className="form-input-fields">
                                <input
                                    type="text"
                                    className={`form-control ${errors.employer ? 'is-invalid' : ''}`}
                                    {...register('employer')}
                                />
                                {errors.employer && (
                                    <div className="invalid-feedback">{errors.employer.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="form-input-wrapper">
                            <span className="span-bold">{t('persons.employer.country')}<span className="required">*</span></span>
                            <div className="form-input-fields">
                                <Select
                                    options={countries}
                                    className={`select-container ${errors.country ? 'is-invalid' : ''}`}
                                    classNamePrefix="select"
                                    placeholder={t('common.select')}
                                    onChange={handleCountryChange}
                                    value={selectedCountry}
                                    isSearchable={true}
                                />
                                {errors.country && (
                                    <div className="invalid-feedback">{errors.country.message}</div>
                                )}
                            </div>
                        </div>

                        <div className="employer-form-controls">
                            <button 
                                type="submit" 
                                className={`form-btn ${isEditing ? 'btn-orange' : 'btn-green'}`}
                            >
                                {isEditing ? t('common.edit') : t('common.add')}
                            </button>
                            {isEditing && (
                                <button 
                                    type="button" 
                                    className="btn-gray form-btn" 
                                    onClick={handleCancel}
                                >
                                    {t('common.cancel')}
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                <div className="table-section">
                    <div className="employer-list-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('persons.employer.startDate')}</th>
                                    <th>{t('persons.employer.endDate')}</th>
                                    <th>{t('persons.employer.position')}</th>
                                    <th>{t('persons.employer.employer')}</th>
                                    <th>{t('persons.employer.country')}</th>
                                    <th>{t('common.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employerData?.map((employer) => (
                                    <tr key={employer.id}>
                                        <td>{formatDate(employer.startDate)}</td>
                                        <td>{formatDate(employer.endDate)}</td>
                                        <td>{employer.position}</td>
                                        <td>{employer.employer}</td>
                                        <td>{employer.country}</td>
                                        <td className="action-buttons">
                                            <FontAwesomeIcon 
                                                icon={faEdit} 
                                                className="action-icon edit"
                                                onClick={() => handleEdit(employer)}
                                            />
                                            <FontAwesomeIcon 
                                                icon={faTrash} 
                                                className="action-icon delete"
                                                onClick={() => handleDelete(employer.id)}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
} 